import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import UploadAssetModal from "@/components/UploadAssetModal";
import BulkUploadModal from "@/components/BulkUploadModal";
import EditAssetModal from "@/components/EditAssetModal";
import BulkAssignModal from "@/components/BulkAssignModal";
import BackButton from "@/components/BackButton";
import { useAuth } from "@/context/AuthContext";
import { Search, Plus, Layers, Inbox, Pencil, Trash2, FileType2, ArrowRight, CheckSquare, Square, X } from "lucide-react";
import { confirmDialog } from "@/components/ConfirmDialog";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

export default function MediaLibrary() {
  const { user } = useAuth();
  const [assets, setAssets] = useState([]);
  const [q, setQ] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [sort, setSort] = useState("recent"); // recent | az | za
  const canManage = user && user.role === "admin";

  const load = () =>
    api
      .get("/assets", { params: { unassigned: true, limit: 500 } })
      .then((r) => setAssets(r.data));

  useEffect(() => {
    if (canManage) load();
  }, [canManage]);

  async function handleDelete(asset) {
    const ok = await confirmDialog({
      title: `Delete "${asset.title}"?`,
      description: "This cannot be undone.",
      confirmLabel: "Delete",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/assets/${asset.id}`);
      toast.success("Asset deleted");
      setAssets((prev) => prev.filter((a) => a.id !== asset.id));
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(asset.id);
        return next;
      });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function handleBulkDelete() {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const ok = await confirmDialog({
      title: `Delete ${ids.length} ${ids.length === 1 ? "asset" : "assets"}?`,
      description: "This cannot be undone.",
      confirmLabel: `Delete ${ids.length}`,
    });
    if (!ok) return;
    try {
      await api.post("/admin/assets/bulk-delete", { ids });
      toast.success(`Deleted ${ids.length} ${ids.length === 1 ? "asset" : "assets"}`);
      setAssets((prev) => prev.filter((a) => !ids.includes(a.id)));
      setSelected(new Set());
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  function handleSaved(updated) {
    // If the asset is now assigned, it leaves Media Manager
    const cat = (updated.category || "").toLowerCase();
    const isAssigned = cat && cat !== "unassigned" && ((updated.states || []).length || (updated.allowed_user_ids || []).length);
    if (isAssigned) {
      setAssets((prev) => prev.filter((a) => a.id !== updated.id));
      toast.success(`"${updated.title}" published to ${updated.category}`);
    } else {
      setAssets((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
    }
  }

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    const base = !q
      ? assets
      : assets.filter(
          (a) => a.title.toLowerCase().includes(s) || (a.tags || []).some((t) => t.toLowerCase().includes(s))
        );
    if (sort === "az" || sort === "za") {
      const sorted = [...base].sort((a, b) =>
        (a.title || "").localeCompare(b.title || "", undefined, { sensitivity: "base", numeric: true })
      );
      return sort === "za" ? sorted.reverse() : sorted;
    }
    return base;
  }, [assets, q, sort]);

  if (!canManage) {
    return (
      <div className="p-10 text-center text-muted-foreground" data-testid="library-nopermission">
        Media Manager is admin-only.
      </div>
    );
  }

  return (
    <div className="space-y-8 slide-up" data-testid="library-page">
      <BackButton />
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2 flex items-center gap-2">
            <Inbox className="h-3 w-3" /> Staging Inbox
          </div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl uppercase font-bold tracking-tight">Media Manager</h1>
          <p className="text-muted-foreground mt-2 max-w-xl">
            Newly uploaded assets land here. Assign a <strong>category</strong> and at least one <strong>location</strong> or <strong>user</strong> to publish them into the portal. Once assigned they leave this inbox.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setBulkOpen(true)}
            className="border border-border bg-surface px-4 py-2 text-xs font-semibold tracking-widest uppercase flex items-center gap-2 hover:bg-surface-elev transition"
            data-testid="library-bulk-btn"
          >
            <Layers className="h-3.5 w-3.5" /> Bulk Upload
          </button>
          <button
            onClick={() => setUploadOpen(true)}
            className="bg-foreground text-background px-4 py-2 text-xs font-semibold tracking-widest uppercase flex items-center gap-2"
            data-testid="library-upload-btn"
          >
            <Plus className="h-3.5 w-3.5" /> Upload Asset
          </button>
        </div>
      </header>

      <div className="border border-border rounded-sm bg-surface p-4 flex items-center gap-2">
        <Search className="h-3.5 w-3.5 text-muted-foreground" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search staging inbox"
          className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          data-testid="library-search"
        />
        <div className="hidden md:flex items-center gap-1 border-l border-border pl-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Sort</span>
          {[
            { k: "recent", label: "Recent" },
            { k: "az", label: "A–Z" },
            { k: "za", label: "Z–A" },
          ].map((opt) => (
            <button
              key={opt.k}
              onClick={() => setSort(opt.k)}
              className={`px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em] rounded-sm transition ${
                sort === opt.k
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`sort-${opt.k}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground border-l border-border pl-3 ml-1">
          {filtered.length} of {assets.length}
        </span>
      </div>

      {filtered.length === 0 ? (
        <div
          className="text-center py-24 border border-dashed border-border rounded-sm"
          data-testid="library-empty"
        >
          <Inbox className="h-8 w-8 mx-auto text-muted-foreground" />
          <div className="mt-4 font-display text-2xl uppercase tracking-tight">Inbox clear</div>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            Every asset is assigned and published. Upload new assets to see them appear here for staging.
          </p>
        </div>
      ) : (
        <>
          <SelectionBar
            total={filtered.length}
            selectedCount={selected.size}
            onSelectAll={() => {
              if (selected.size === filtered.length) setSelected(new Set());
              else setSelected(new Set(filtered.map((a) => a.id)));
            }}
            onClear={() => setSelected(new Set())}
            onBulkAssign={() => setBulkAssignOpen(true)}
            onBulkDelete={handleBulkDelete}
          />
          <div className="space-y-2" data-testid="staging-list">
            {filtered.map((a) => (
              <StagingRow
                key={a.id}
                asset={a}
                selected={selected.has(a.id)}
                onToggle={() => {
                  setSelected((prev) => {
                    const next = new Set(prev);
                    if (next.has(a.id)) next.delete(a.id);
                    else next.add(a.id);
                    return next;
                  });
                }}
                onAssign={() => setEditing(a)}
                onDelete={() => handleDelete(a)}
              />
            ))}
          </div>
        </>
      )}

      <UploadAssetModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        defaultCategory=""
        onUploaded={() => load()}
      />
      <BulkUploadModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        defaultCategory=""
        onUploaded={() => load()}
      />
      <EditAssetModal
        open={!!editing}
        asset={editing}
        onClose={() => setEditing(null)}
        onSaved={handleSaved}
      />
      <BulkAssignModal
        open={bulkAssignOpen}
        assets={filtered.filter((a) => selected.has(a.id))}
        onClose={() => setBulkAssignOpen(false)}
        onSaved={(ids) => {
          setAssets((prev) => prev.filter((a) => !ids.includes(a.id)));
          setSelected(new Set());
        }}
      />
    </div>
  );
}

function SelectionBar({ total, selectedCount, onSelectAll, onClear, onBulkAssign, onBulkDelete }) {
  const allSelected = selectedCount === total && total > 0;
  return (
    <div className="sticky top-16 z-10 border border-border rounded-sm bg-surface px-4 py-2 flex items-center justify-between gap-3 shadow-sm" data-testid="selection-bar">
      <button
        onClick={onSelectAll}
        className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground transition"
        data-testid="select-all"
      >
        {allSelected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
        {allSelected ? "Deselect All" : `Select All (${total})`}
      </button>
      {selectedCount > 0 ? (
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground" data-testid="selection-count">
            {selectedCount} selected
          </span>
          <button
            onClick={onClear}
            className="p-1.5 border border-border rounded-sm text-muted-foreground hover:text-foreground text-xs"
            data-testid="selection-clear"
            aria-label="Clear selection"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={onBulkDelete}
            className="px-3 py-1.5 border border-border rounded-sm text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-[#E15C2C] flex items-center gap-1"
            data-testid="bulk-delete-btn"
          >
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </button>
          <button
            onClick={onBulkAssign}
            className="bg-foreground text-background px-4 py-1.5 text-xs font-semibold tracking-widest uppercase flex items-center gap-2"
            data-testid="bulk-assign-btn"
          >
            <ArrowRight className="h-3.5 w-3.5" /> Assign & Publish {selectedCount}
          </button>
        </div>
      ) : (
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground/70 hidden sm:inline">
          Tick rows to bulk-assign
        </span>
      )}
    </div>
  );
}

function StagingRow({ asset, selected, onToggle, onAssign, onDelete }) {
  const missingCategory = !asset.category || asset.category.toLowerCase() === "unassigned";
  const missingScope = !(asset.states || []).length && !(asset.allowed_user_ids || []).length;

  return (
    <div
      className={`border rounded-sm bg-surface p-4 flex flex-col md:flex-row md:items-center gap-4 hover:bg-surface-elev transition ${
        selected ? "border-foreground shadow-[0_0_0_1px_var(--foreground)]" : "border-border"
      }`}
      data-testid={`staging-row-${asset.id}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="p-1 text-muted-foreground hover:text-foreground shrink-0"
        data-testid={`staging-select-${asset.id}`}
        aria-label={selected ? "Deselect" : "Select"}
      >
        {selected ? <CheckSquare className="h-4 w-4 text-foreground" /> : <Square className="h-4 w-4" />}
      </button>

      <div className="w-16 h-16 shrink-0 bg-surface-elev rounded-sm overflow-hidden flex items-center justify-center">
        {asset.preview_url ? (
          <img
            src={asset.preview_url.startsWith("http") ? asset.preview_url : `${process.env.REACT_APP_BACKEND_URL}${asset.preview_url}`}
            alt={asset.title}
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <FileType2 className="h-6 w-6 text-muted-foreground" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate">{asset.title}</div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
          <span>{asset.file_type}</span>
          <span className="w-1 h-1 rounded-full bg-muted-foreground/40" />
          {missingCategory ? (
            <span className="text-[#E15C2C]">No category</span>
          ) : (
            <span>{asset.category}</span>
          )}
          <span className="w-1 h-1 rounded-full bg-muted-foreground/40" />
          {missingScope ? (
            <span className="text-[#E15C2C]">No location / user</span>
          ) : (
            <span>
              {(asset.states || []).join(", ")}
              {(asset.states || []).length && (asset.allowed_user_ids || []).length ? " · " : ""}
              {(asset.allowed_user_ids || []).length ? `${asset.allowed_user_ids.length} user(s)` : ""}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={onAssign}
          className="bg-foreground text-background px-4 py-2 text-xs font-semibold tracking-widest uppercase flex items-center gap-2"
          data-testid={`staging-assign-${asset.id}`}
        >
          <ArrowRight className="h-3.5 w-3.5" /> Assign & Publish
        </button>
        <button
          onClick={onAssign}
          className="p-2 border border-border rounded-sm hover:bg-surface-elev text-muted-foreground"
          data-testid={`staging-edit-${asset.id}`}
          aria-label="Edit"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={onDelete}
          className="p-2 border border-border rounded-sm hover:bg-surface-elev text-muted-foreground hover:text-[#E15C2C]"
          data-testid={`staging-delete-${asset.id}`}
          aria-label="Delete"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
